import { useMemo } from 'react';
import {
  CheckIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import {
  PortalPlanningData,
  PortalPlanningMilestone,
  PortalPlanningPhase,
  PortalPlanningTask,
  PortalPlanningTaskStatus,
} from './portalPlanningMock';

const statusLabel: Record<PortalPlanningTaskStatus, string> = {
  done: 'Concluído',
  inProgress: 'Em andamento',
  planned: 'Planejado',
  notStarted: 'Não iniciado',
};

const statusClass: Record<PortalPlanningTaskStatus, string> = {
  done: 'bg-emerald-50 text-emerald-700',
  inProgress: 'bg-primary-50 text-primary-700',
  planned: 'bg-sky-50 text-sky-700',
  notStarted: 'bg-secondary-100 text-secondary-600',
};

const milestoneStatusLabel: Record<PortalPlanningMilestone['status'], string> = {
  done: 'Concluído',
  current: 'Em andamento',
  pending: 'Planejado',
};

const milestoneStatusClass: Record<PortalPlanningMilestone['status'], string> = {
  done: 'bg-emerald-50 text-emerald-700',
  current: 'bg-primary-50 text-primary-700',
  pending: 'bg-secondary-100 text-secondary-600',
};

const weekdayLabels = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function parseDate(iso: string): Date {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function formatLongDate(iso: string): string {
  return parseDate(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatShortDate(iso: string): string {
  return parseDate(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
}

function monthTitle(date: Date): string {
  const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

type FlatScheduleRow =
  | { kind: 'phase'; phase: PortalPlanningPhase }
  | { kind: 'task'; phase: PortalPlanningPhase; task: PortalPlanningTask };

function flattenSchedule(phases: PortalPlanningPhase[]): FlatScheduleRow[] {
  return phases.flatMap((phase) => [
    { kind: 'phase' as const, phase },
    ...phase.tasks.map((task) => ({ kind: 'task' as const, phase, task })),
  ]);
}

type ScheduleListProps = {
  phases: PortalPlanningPhase[];
};

export function PlanningScheduleList({ phases }: ScheduleListProps) {
  const rows = useMemo(() => flattenSchedule(phases), [phases]);

  return (
    <div className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-secondary-200 bg-secondary-50 text-xs font-medium text-secondary-500">
            <tr>
              <th className="px-4 py-3 font-medium">Atividade</th>
              <th className="px-3 py-3 font-medium">Fase</th>
              <th className="px-3 py-3 font-medium">Responsável</th>
              <th className="px-3 py-3 font-medium">Início</th>
              <th className="px-3 py-3 font-medium">Término</th>
              <th className="px-3 py-3 font-medium">Progresso</th>
              <th className="px-3 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              if (row.kind === 'phase') {
                const { phase } = row;
                return (
                  <tr key={`phase-${phase.id}`} className="border-t border-secondary-100 bg-secondary-50/80">
                    <td className="px-4 py-3 font-semibold text-secondary-900">
                      {phase.number}. {phase.title}
                    </td>
                    <td className="px-3 py-3 text-secondary-500">—</td>
                    <td className="px-3 py-3 text-secondary-500">—</td>
                    <td className="whitespace-nowrap px-3 py-3 text-secondary-700">
                      {formatShortDate(phase.startDate)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-secondary-700">
                      {formatShortDate(phase.endDate)}
                    </td>
                    <td className="px-3 py-3">
                      <ProgressCell percent={phase.progressPercent} />
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClass[phase.status]}`}>
                        {statusLabel[phase.status]}
                      </span>
                    </td>
                  </tr>
                );
              }

              const { phase, task } = row;
              return (
                <tr key={task.id} className="border-t border-secondary-100">
                  <td className="px-4 py-3 pl-8 text-secondary-800">
                    <span className="inline-flex items-center gap-2">
                      {task.status === 'done' ? (
                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white">
                          <CheckIcon className="h-3 w-3" aria-hidden />
                        </span>
                      ) : (
                        <span className="h-4 w-4 rounded-full border border-secondary-300" />
                      )}
                      {task.title}
                      {task.isMilestone ? (
                        <FlagIcon className="h-3.5 w-3.5 text-secondary-500" aria-hidden />
                      ) : null}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-secondary-600">{phase.title}</td>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                        {initials(task.ownerName)}
                      </span>
                      <span className="whitespace-nowrap text-secondary-700">{task.ownerName}</span>
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-secondary-700">
                    {formatShortDate(task.startDate)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-secondary-700">
                    {formatShortDate(task.endDate)}
                  </td>
                  <td className="px-3 py-3">
                    <ProgressCell percent={task.progressPercent} />
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClass[task.status]}`}>
                      {statusLabel[task.status]}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProgressCell({ percent }: { percent: number }) {
  return (
    <div className="min-w-[6rem]">
      <div className="mb-1 flex items-center justify-between text-xs text-secondary-500">
        <span className="font-semibold text-secondary-700">{percent}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-secondary-100">
        <div className="h-full rounded-full bg-primary-500" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

type CalendarEvent = {
  id: string;
  title: string;
  date: string;
  tone: 'done' | 'progress' | 'planned' | 'milestone';
  kindLabel: string;
};

type PlanningCalendarProps = {
  planning: PortalPlanningData;
  visibleMonth: Date;
};

export function PlanningCalendar({ planning, visibleMonth }: PlanningCalendarProps) {
  const events = useMemo(() => {
    const items: CalendarEvent[] = [];
    for (const phase of planning.phases) {
      for (const task of phase.tasks) {
        items.push({
          id: `${task.id}-start`,
          title: task.title,
          date: task.startDate,
          tone: task.isMilestone
            ? 'milestone'
            : task.status === 'done'
              ? 'done'
              : task.status === 'inProgress'
                ? 'progress'
                : 'planned',
          kindLabel: task.isMilestone ? 'Marco' : 'Início',
        });
        if (task.endDate !== task.startDate) {
          items.push({
            id: `${task.id}-end`,
            title: task.title,
            date: task.endDate,
            tone: task.isMilestone
              ? 'milestone'
              : task.status === 'done'
                ? 'done'
                : task.status === 'inProgress'
                  ? 'progress'
                  : 'planned',
            kindLabel: task.isMilestone ? 'Marco' : 'Término',
          });
        }
      }
    }
    for (const milestone of planning.milestones) {
      const alreadyListed = items.some(
        (item) => item.date === milestone.date && item.title === milestone.title,
      );
      if (!alreadyListed) {
        items.push({
          id: milestone.id,
          title: milestone.title,
          date: milestone.date,
          tone: 'milestone',
          kindLabel: 'Marco',
        });
      }
    }
    return items;
  }, [planning.milestones, planning.phases]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      const list = map.get(event.date) ?? [];
      list.push(event);
      map.set(event.date, list);
    }
    return map;
  }, [events]);

  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = (firstDay.getDay() + 6) % 7;
  const cells: Array<{ date: Date | null; key: string }> = [];

  for (let index = 0; index < startWeekday; index += 1) {
    cells.push({ date: null, key: `empty-start-${index}` });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, month, day);
    cells.push({ date, key: toDateKey(date) });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ date: null, key: `empty-end-${cells.length}` });
  }

  const todayKey = planning.todayDate;
  const monthEvents = events
    .filter((event) => {
      const date = parseDate(event.date);
      return date.getFullYear() === year && date.getMonth() === month;
    })
    .sort((left, right) => left.date.localeCompare(right.date));

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_20rem]">
      <div className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
        <div className="border-b border-secondary-100 px-5 py-4">
          <h3 className="text-base font-semibold text-secondary-900">{monthTitle(visibleMonth)}</h3>
          <p className="mt-1 text-sm text-secondary-500">
            Atividades, termos e marcos do mês selecionado.
          </p>
        </div>
        <div className="grid grid-cols-7 border-b border-secondary-100 bg-secondary-50">
          {weekdayLabels.map((label) => (
            <div key={label} className="px-2 py-2 text-center text-xs font-semibold text-secondary-500">
              {label}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((cell) => {
            if (!cell.date) {
              return <div key={cell.key} className="min-h-[6.5rem] border-b border-r border-secondary-100 bg-secondary-50/40" />;
            }
            const key = toDateKey(cell.date);
            const dayEvents = eventsByDay.get(key) ?? [];
            const isToday = key === todayKey;
            return (
              <div
                key={cell.key}
                className={`min-h-[6.5rem] border-b border-r border-secondary-100 p-2 ${
                  isToday ? 'bg-primary-50/50' : 'bg-white'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                      isToday ? 'bg-primary-500 text-white' : 'text-secondary-700'
                    }`}
                  >
                    {cell.date.getDate()}
                  </span>
                  {dayEvents.some((event) => event.tone === 'milestone') ? (
                    <FlagIcon className="h-3.5 w-3.5 text-secondary-500" aria-hidden />
                  ) : null}
                </div>
                <ul className="mt-1 space-y-1">
                  {dayEvents.slice(0, 3).map((event) => (
                    <li
                      key={event.id}
                      className={`truncate rounded px-1.5 py-0.5 text-[10px] font-medium ${eventToneClass(event.tone)}`}
                      title={`${event.kindLabel}: ${event.title}`}
                    >
                      {event.title}
                    </li>
                  ))}
                  {dayEvents.length > 3 ? (
                    <li className="px-1 text-[10px] font-medium text-secondary-500">
                      +{dayEvents.length - 3} mais
                    </li>
                  ) : null}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      <aside className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
        <h3 className="text-base font-semibold text-secondary-900">Neste mês</h3>
        {monthEvents.length === 0 ? (
          <p className="mt-3 text-sm text-secondary-500">Nenhuma atividade neste mês.</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {monthEvents.map((event) => (
              <li key={event.id} className="flex gap-3">
                <span className={`mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${eventDotClass(event.tone)}`} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-secondary-900">{event.title}</p>
                  <p className="text-xs text-secondary-500">
                    {event.kindLabel} · {formatLongDate(event.date)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
}

function eventToneClass(tone: CalendarEvent['tone']): string {
  if (tone === 'done') return 'bg-emerald-50 text-emerald-700';
  if (tone === 'progress') return 'bg-primary-50 text-primary-700';
  if (tone === 'milestone') return 'bg-violet-50 text-violet-700';
  return 'bg-sky-50 text-sky-700';
}

function eventDotClass(tone: CalendarEvent['tone']): string {
  if (tone === 'done') return 'bg-emerald-500';
  if (tone === 'progress') return 'bg-primary-500';
  if (tone === 'milestone') return 'bg-violet-500';
  return 'bg-sky-400';
}

type MilestonesListProps = {
  milestones: PortalPlanningMilestone[];
  todayDate: string;
};

export function PlanningMilestonesList({ milestones, todayDate }: MilestonesListProps) {
  const today = parseDate(todayDate);

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
        <div className="border-b border-secondary-100 px-5 py-4">
          <h3 className="text-base font-semibold text-secondary-900">Listagem de marcos</h3>
          <p className="mt-1 text-sm text-secondary-500">
            Marcos compartilhados do projeto, com status e datas previstas.
          </p>
        </div>
        <ul className="divide-y divide-secondary-100">
          {milestones.map((milestone, index) => {
            const date = parseDate(milestone.date);
            const isPast = date < today && milestone.status !== 'done';
            return (
              <li key={milestone.id} className="flex flex-wrap items-start gap-4 px-5 py-4">
                <div className="flex w-16 shrink-0 flex-col items-center">
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                      milestone.status === 'done'
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : milestone.status === 'current'
                          ? 'border-primary-500 bg-white'
                          : 'border-secondary-300 bg-white'
                    }`}
                  >
                    {milestone.status === 'done' ? (
                      <CheckIcon className="h-4 w-4" aria-hidden />
                    ) : milestone.status === 'current' ? (
                      <span className="h-2.5 w-2.5 rounded-full bg-primary-500" />
                    ) : (
                      <FlagIcon className="h-3.5 w-3.5 text-secondary-400" aria-hidden />
                    )}
                  </span>
                  {index < milestones.length - 1 ? (
                    <span className="mt-1 h-full min-h-[1.5rem] w-px bg-secondary-200" aria-hidden />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-sm font-semibold text-secondary-900">{milestone.title}</h4>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${milestoneStatusClass[milestone.status]}`}
                    >
                      {milestoneStatusLabel[milestone.status]}
                    </span>
                    {isPast ? (
                      <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                        Em atraso
                      </span>
                    ) : null}
                  </div>
                  {milestone.description ? (
                    <p className="mt-1 text-sm text-secondary-600">{milestone.description}</p>
                  ) : null}
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium text-secondary-500">Data prevista</p>
                  <p className="mt-0.5 text-sm font-semibold text-secondary-900">
                    {formatLongDate(milestone.date)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
